import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import {
  CheckCircle2, AlertCircle, MinusCircle, Loader2, ArrowRight, Rocket, ShieldCheck,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  done: { color: '#16a34a', bg: 'bg-emerald-50 border-emerald-200', icon: CheckCircle2, label: 'Done' },
  pending: { color: '#dc2626', bg: 'bg-red-50 border-red-200', icon: AlertCircle, label: 'Pending' },
  optional: { color: '#64748b', bg: 'bg-slate-50 border-slate-200', icon: MinusCircle, label: 'Optional' },
  info: { color: '#64748b', bg: 'bg-slate-50 border-slate-200', icon: MinusCircle, label: 'Info' },
};

const SuperAdminSetup = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/setup-checklist`, { withCredentials: true });
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8 max-w-4xl mx-auto" data-testid="super-admin-setup-page">
        <header className="mb-6">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
            Launch checklist
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            Get CivilX TMS production-ready in a few minutes. Items marked <strong>pending</strong> should be completed before onboarding real customers.
          </p>
        </header>

        {loading ? (
          <div className="bg-white border border-slate-200 rounded-md p-10 flex items-center gap-2 text-sm text-slate-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Checking your setup…
          </div>
        ) : !data ? (
          <div className="bg-red-50 border border-red-200 rounded-md p-6 text-sm text-red-700" data-testid="setup-error">
            Could not load checklist. Try refreshing the page.
          </div>
        ) : (
          <>
            {/* Progress hero */}
            <div className={`p-6 mb-6 rounded-md border ${data.ready_to_launch ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`} data-testid="setup-progress-hero">
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-full ${data.ready_to_launch ? 'bg-emerald-100' : 'bg-amber-100'} flex items-center justify-center flex-shrink-0`}>
                  {data.ready_to_launch
                    ? <Rocket className="w-6 h-6 text-emerald-600" strokeWidth={2.25} />
                    : <ShieldCheck className="w-6 h-6 text-amber-600" strokeWidth={2.25} />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                    {data.progress.done} of {data.progress.total} complete
                  </p>
                  <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                    {data.ready_to_launch ? 'Ready to launch!' : 'A few items still need attention'}
                  </h2>
                </div>
                <div className="w-32 hidden sm:block">
                  <div className="h-2 bg-white rounded-full overflow-hidden border border-slate-200">
                    <div
                      data-testid="setup-progress-bar"
                      className={`h-full ${data.ready_to_launch ? 'bg-emerald-500' : 'bg-amber-500'}`}
                      style={{ width: `${Math.round((data.progress.done / data.progress.total) * 100)}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Items */}
            <ul className="space-y-3" data-testid="setup-items-list">
              {data.items.map((item) => {
                const meta = STATUS_META[item.status] || STATUS_META.info;
                const Icon = meta.icon;
                return (
                  <li
                    key={item.id}
                    data-testid={`setup-item-${item.id}`}
                    className={`p-4 rounded-md border flex items-start gap-3 ${meta.bg}`}
                  >
                    <Icon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: meta.color }} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                          {item.title}
                        </p>
                        <span className="text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded" style={{ color: meta.color, backgroundColor: 'rgba(255,255,255,0.7)' }}>
                          {meta.label}
                        </span>
                        {item.blocking && item.status === 'pending' && (
                          <span className="text-[10px] font-bold uppercase tracking-widest text-red-700 bg-red-100 px-1.5 py-0.5 rounded">
                            Blocking
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-600 mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                        {item.description}
                      </p>
                    </div>
                    {item.deeplink && (
                      <Link
                        to={item.deeplink}
                        data-testid={`setup-item-${item.id}-cta`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 rounded text-xs font-bold flex-shrink-0"
                        style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                      >
                        Open <ArrowRight className="w-3 h-3" />
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default SuperAdminSetup;
